import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';

export interface ListItem extends TreeViewBaseItem<ExtendedTreeItemProps> {
  selected?: boolean;
  children?: ListItem[];
  parent?: string | null;
  type?: string;
  isGroup?: string;
  isLocked?: boolean;
  text?: string;
  securityGroupId?: string | null;
}

// Define common item shapes from API
export interface ApiItem {
  id: string;
  text: string;
  parent?: string;
  type?: string;
  isGroup?: string;
  isLocked?: boolean;
  securityGroupId?: string;
}

export interface SelectedItem {
  id: string;
  text: string;
  securityGroupId?: string;
}

// Helper: Build hierarchical (tree-style) structure
const buildTree = (
  items: ApiItem[] = [],
  selectedItems: SelectedItem[] = []
): ListItem[] => {
  const map = new Map<string, ListItem>();
  const roots: ListItem[] = [];

  // Create node map
  for (const item of items) {
    map.set(item.id, {
      id: item.id,
      label: item.text,
      selected: selectedItems.some((sel: SelectedItem) => sel.id === item.id),
      parent: item.parent ?? null,
      type: item.type ?? '',
      isGroup: item.isGroup ?? 'false',
      isLocked: item.isLocked ?? false,
      text: item.text ?? item.id,
      children: [],
      securityGroupId: item.securityGroupId ?? null,
    });
  }

  // Establish parent-child relationships
  for (const item of items) {
    const node = map.get(item.id);
    if (!node) continue;

    if (item.parent && item.parent !== '#' && map.has(item.parent)) {
      map.get(item.parent)!.children!.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
};

// Helper: Build simple flat lists (no hierarchy)
const buildFlatList = (
  items: ApiItem[] = [],
  selectedItems: SelectedItem[] = []
): ListItem[] => {
  return items.map((item) => ({
    id: item.id,
    label: item.text,
    selected: selectedItems.some((sel: SelectedItem) => sel.id === item.id),
    parent: item.parent ?? null,
    type: item.type ?? '',
    isGroup: item.isGroup ?? 'false',
    isLocked: item.isLocked ?? false,
    text: item.text ?? item.id,
    securityGroupId: item.securityGroupId ?? null,
  }));
};

// Define a strong return type
export type FolderTreeData = Record<
  string,
  TreeViewBaseItem<ExtendedTreeItemProps>[]
>;

// Main Transformer Function
export const buildFolderTree = (
  apiData: Record<string, any>
): FolderTreeData => {
  if (!apiData) return {} as FolderTreeData;

  return {
    FolderList: buildTree(
      apiData.folderList ?? [],
      apiData.folderList_Selected ?? []
    ),
    FolderListSelected: buildFlatList(apiData.folderList_Selected ?? []),

    KeywordsList: buildTree(
      apiData.keyWordList ?? [],
      apiData.keyWordList_Selected ?? []
    ),
    KeywordsListSelected: buildFlatList(apiData.keyWordList_Selected ?? []),

    TagsList: buildTree(apiData.tagList ?? [], apiData.tagList_Selected ?? []),
    TagsListSelected: buildFlatList(apiData.tagList_Selected ?? []),

    CommentsList: buildTree(
      apiData.commentList ?? [],
      apiData.commentList_Selected ?? []
    ),
    CommentsListSelected: buildFlatList(apiData.commentList_Selected ?? []),

    AnnotationLayersList: buildTree(
      apiData.annotationList ?? [],
      apiData.annotationList_Selected ?? []
    ),
    AnnotationLayersListSelected: buildFlatList(
      apiData.annotationList_Selected ?? []
    ),

    ProjectFieldsList: buildTree(
      apiData.projectFieldsList ?? [],
      apiData.projectFieldsList_Selected ?? []
    ),
    ProjectFieldsListSelected: buildFlatList(
      apiData.projectFieldsList_Selected ?? []
    ),

    RedactionTagsList: buildTree(
      apiData.redactionList ?? [],
      apiData.redactionList_Selected ?? []
    ),
    RedactionTagsListSelected: buildFlatList(
      apiData.redactionList_Selected ?? []
    ),

    SecurityList: buildTree(
      apiData.assignmentList ?? [],
      apiData.assignmentList_Selected ?? []
    ),
    SecurityListSelected: buildFlatList(apiData.assignmentList_Selected ?? []),
  } satisfies FolderTreeData;
};
